import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import DashboardNav from '@/components/DashboardNav';
import PasswordInput from '@/components/PasswordInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Camera, Check, Lock, User, Mail, AtSign } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, token, updateUser } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.username || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);

  const isAdmin = user?.role === 'admin';
  const initials = `${(user?.first_name || '')[0] || ''}${(user?.last_name || '')[0] || ''}`.toUpperCase();
  const profileImg = user?.profile_image ? `${API}/files/${user.profile_image}` : '';

  const handleProfileUpdate = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, { first_name: firstName, last_name: lastName, email }, headers);
      updateUser({ first_name: firstName, last_name: lastName, email });
      toast.success(t('profile_updated'));
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
    finally { setSavingProfile(false); }
  };

  const handleUsernameChange = async () => {
    if (!username.trim()) {
      toast.error('El nombre de usuario no puede estar vacio');
      return;
    }
    setSavingUsername(true);
    try {
      await axios.put(`${API}/auth/change-username`, { new_username: username }, headers);
      updateUser({ username });
      toast.success('Nombre de usuario actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
    finally { setSavingUsername(false); }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Las contrasenas no coinciden');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, headers);
      toast.success(t('password_changed'));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
    finally { setSavingPassword(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/auth/profile-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ profile_image: res.data.profile_image });
      toast.success(t('profile_updated'));
    } catch { toast.error(t('error')); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      <DashboardNav />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Profile Image */}
        <Card className="rounded-xl">
          <CardHeader><CardTitle style={{ fontFamily: 'Montserrat' }}>{t('profile_image')}</CardTitle></CardHeader>
          <CardContent className="flex items-center gap-6">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-4 border-blue-100">
                <AvatarImage src={profileImg} alt={user?.first_name} />
                <AvatarFallback className="bg-blue-500 text-white text-2xl font-bold">{initials}</AvatarFallback>
              </Avatar>
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera className="w-6 h-6 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="profile-image-input" />
              </label>
            </div>
            <div>
              <p className="font-semibold text-lg">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500 capitalize">{t(user?.role || '')}</p>
              <p className="text-sm text-slate-400">@{user?.username}</p>
            </div>
          </CardContent>
        </Card>

        {/* Change Username - Only for Admin */}
        {isAdmin && (
          <Card className="rounded-xl border-blue-200 bg-blue-50/50">
            <CardHeader><CardTitle style={{ fontFamily: 'Montserrat' }} className="flex items-center gap-2"><AtSign className="w-5 h-5 text-blue-500" /> Cambiar nombre de usuario</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nuevo nombre de usuario</Label>
                <div className="relative">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="change-username-input" value={username} onChange={e => setUsername(e.target.value)} className="pl-10" />
                </div>
                <p className="text-xs text-slate-500 mt-1">Solo disponible para administradores</p>
              </div>
              <Button data-testid="save-username-btn" onClick={handleUsernameChange} disabled={savingUsername} className="rounded-xl">
                {savingUsername ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Guardar nombre de usuario'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Personal Data */}
        <Card className="rounded-xl">
          <CardHeader><CardTitle style={{ fontFamily: 'Montserrat' }}>{t('personal_data')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('first_name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="profile-firstname" value={firstName} onChange={e => setFirstName(e.target.value)} className="pl-10" />
                </div>
              </div>
              <div>
                <Label>{t('last_name')}</Label>
                <Input data-testid="profile-lastname" value={lastName} onChange={e => setLastName(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="profile-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" />
              </div>
              <div className="mt-1">
                {user?.email_verified
                  ? <Badge className="bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 mr-1" />{t('email_verified')}</Badge>
                  : <Badge variant="secondary">{t('email_not_verified')}</Badge>
                }
              </div>
            </div>
            <Button data-testid="save-profile-btn" onClick={handleProfileUpdate} disabled={savingProfile} className="rounded-xl">
              {savingProfile ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('save')}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="rounded-xl">
          <CardHeader><CardTitle style={{ fontFamily: 'Montserrat' }}>{t('change_password')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('current_password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput data-testid="current-password-input" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="pl-10 pr-24" />
              </div>
            </div>
            <div>
              <Label>{t('new_password')}</Label>
              <PasswordInput data-testid="new-password-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pr-24" />
            </div>
            <div>
              <Label>{t('confirm_password')}</Label>
              <PasswordInput data-testid="confirm-password-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pr-24" />
            </div>
            <Button data-testid="change-password-btn" onClick={handlePasswordChange} disabled={savingPassword} variant="outline" className="rounded-xl">
              {savingPassword ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : t('change_password')}
            </Button>
          </CardContent>
        </Card>

        {/* Gamification removed: Mi Perfil is for personal config only.
            Las insignias se muestran en /progreso (accesible desde el badge superior). */}
      </main>
    </div>
  );
}
